import { Suspense, type ReactNode } from "react";
import {
  DashboardSkeleton,
  AssetListSkeleton,
  AssetDetailSkeleton,
  InsightsSkeleton,
  LocationsSkeleton,
  SettingsSkeleton,
} from "@/components/PageSkeletons";
import { Skeleton } from "@/components/ui/skeleton";

/** Generic page-level skeleton for routes without a custom one */
function GenericSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

/** Location detail page skeleton (header + asset grid) */
function LocationDetailSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <Skeleton className="h-11 w-11 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-36" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-40 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

type SkeletonVariant =
  | "dashboard"
  | "asset-list"
  | "asset-detail"
  | "insights"
  | "locations"
  | "location-detail"
  | "settings"
  | "generic";

const skeletonMap: Record<SkeletonVariant, () => ReactNode> = {
  dashboard: () => <DashboardSkeleton />,
  "asset-list": () => <AssetListSkeleton />,
  "asset-detail": () => <AssetDetailSkeleton />,
  insights: () => <InsightsSkeleton />,
  locations: () => <LocationsSkeleton />,
  "location-detail": () => <LocationDetailSkeleton />,
  settings: () => <SettingsSkeleton />,
  generic: () => <GenericSkeleton />,
};

interface RouteSuspenseProps {
  skeleton?: SkeletonVariant;
  children: ReactNode;
}

/**
 * Per-route Suspense wrapper that renders a page-specific skeleton
 * while the lazy-loaded route chunk is being fetched.
 *
 * Note: No extra padding is applied here — AppLayout already provides
 * px-4 py-6 on its <main> element.
 */
export default function RouteSuspense({
  skeleton = "generic",
  children,
}: RouteSuspenseProps) {
  return (
    <Suspense fallback={skeletonMap[skeleton]()}>
      {children}
    </Suspense>
  );
}
